<script setup lang="ts">
import { computed } from "vue";
import AnalysisFrame from "./AnalysisFrame.vue";
import PartZoneLayer from "./PartZoneLayer.vue";
import { useObservationFrames } from "@/composable/useObservationFrames";
import { pickObservationFrames } from "@/stores/ObservationsPanelState";
import { usePlanAnalysisState } from "@/stores/PlanAnalysisState";
import { idsCenteredIn, type ScreenZone } from "@/utils/observation-plan";

/**
 * Surcouche HTML des cadres BLEUS des observations, posée DANS le conteneur
 * du canvas (même géométrie que les cadres violets de l'analyse). Visible
 * devant les onglets Observations et Parties du volet : ce sont eux qui
 * fournissent les cadres, et il n'y en a plus dès qu'ils sont fermés. Les
 * gestes (badge touché, zone tracée) sont remis à l'onglet affiché, qui sait
 * quoi en faire. Rien n'est écrit dans le .dwg.
 */

const { frames } = useObservationFrames();
const state = usePlanAnalysisState();

/**
 * Projecteur sur l'observation mise en avant : un voile assombrit tout le
 * plan SAUF son cadre (ombre géante autour d'un trou) — même effet que pour
 * le groupe d'analyse courant.
 */
const spotlightFrame = computed(
  () => frames.value.find((frame) => frame.isCurrent) ?? null,
);

/** Mode association de l'onglet Parties, zone de sélection armée. */
const zoneArmed = computed(
  () => state.assignPartId !== null && state.zoneSelectMode,
);

const handleBadge = (observationLocalId: string): void => {
  pickObservationFrames({ kind: "badge", ids: [observationLocalId] });
};

/** Un trait désarme la zone, qu'il ait couvert des cadres ou non. */
const handleZone = (zone: ScreenZone | null): void => {
  state.zoneSelectMode = false;
  if (!zone) return;
  const ids = idsCenteredIn(frames.value, zone);
  if (ids.length) pickObservationFrames({ kind: "zone", ids });
};
</script>

<template>
  <div v-if="frames.length || zoneArmed" class="observation-frames-overlay">
    <PartZoneLayer v-if="zoneArmed" @zone="handleZone" />
    <div
      v-if="spotlightFrame"
      class="spotlight"
      :style="{
        left: `${spotlightFrame.left}px`,
        top: `${spotlightFrame.top}px`,
        width: `${spotlightFrame.width}px`,
        height: `${spotlightFrame.height}px`,
      }"
    />
    <AnalysisFrame
      v-for="frame in frames"
      :key="frame.id"
      :frame="frame"
      @select="handleBadge"
    />
  </div>
</template>

<style lang="scss" scoped>
.observation-frames-overlay {
  position: absolute;
  inset: 0;
  overflow: hidden;
  // Les cadres gèrent eux-mêmes leurs zones interactives (badges).
  pointer-events: none;
}

.spotlight {
  position: absolute;
  border-radius: 6px;
  // L'ombre démesurée fait le voile ; le trou, c'est l'élément lui-même.
  box-shadow: 0 0 0 100000px rgba(8, 6, 18, 0.42);
  pointer-events: none;
}
</style>
